import { useState, useCallback } from 'react';
import { message } from 'antd';
import { c5m2Api, WriteoffItem } from '../../../services/c5m2Api';

export interface ItemState {
  item: WriteoffItem;
  selected: boolean;
}

export function useC5M2() {
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [queried, setQueried] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  /** Query S/C items by range (empty = all eligible) */
  const handleQuery = useCallback(async (startNo: string, endNo: string) => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await c5m2Api.query(startNo.trim(), endNo.trim());
      setLastApiResponse(res);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的S/C');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Toggle checkbox for a single item */
  const handleToggleItem = useCallback((scNo: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fc110_01 === scNo ? { ...s, selected: checked } : s
      )
    );
  }, []);

  /** Select / deselect all items */
  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  /** Execute writeoff for all selected items */
  const handleExecute = useCallback(async (): Promise<boolean> => {
    const selectedNos = items
      .filter((s) => s.selected)
      .map((s) => s.item.fc110_01);

    if (selectedNos.length === 0) {
      message.warning('請先勾選要沖銷的S/C');
      return false;
    }

    setExecuteLoading(true);
    try {
      const res = await c5m2Api.execute(selectedNos);
      setLastApiResponse(res);
      if (res.failed === 0) {
        message.success(res.message || `沖銷完成，成功 ${res.success} 筆`);
      } else {
        message.warning(
          res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
        );
        if (res.errors && res.errors.length > 0) {
          res.errors.forEach((err) => message.error(err));
        }
      }
      // Clear items after execution
      setItems([]);
      setQueried(false);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '沖銷執行失敗');
      return false;
    } finally {
      setExecuteLoading(false);
    }
  }, [items]);

  const selectedCount = items.filter((s) => s.selected).length;

  return {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    lastApiResponse,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleExecute,
  };
}
